import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, EmptyState, Loading, PageHeader } from '../ui/index.js';
import { QueueRail, type QueueFilters } from './shop-manager/QueueRail.js';
import { OrderTable } from './shop-manager/OrderTable.js';
import { OrderInspector, type InspectorActions } from './shop-manager/OrderInspector.js';
import type { EditorPatch, PreviewResult } from './shop-manager/OrderEditor.js';
import { ShopApiError, shopJson, shopPatch, shopPost } from './shop-manager/client.js';
import type { AttemptOutcome, EditResult, ShopOrderDetail, ShopOrdersResponse } from './shop-manager/types.js';
import { relative } from './shop-manager/presentation.js';

const PAGE_SIZE = 20;

interface PreviewPayload extends EditorPatch {
  readonly dryRun: boolean;
}

interface PreviewResponse {
  readonly status: 'preview';
  readonly diff: readonly string[];
  readonly totalBefore: string | null;
  readonly totalAfter: number | null;
}

export function ShopManagerPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const searchRef = useRef<HTMLInputElement>(null);

  const [filters, setFilters] = useState<QueueFilters>({
    view: 'pending',
    assigned: 'all',
    wooStatus: 'all',
    search: '',
  });
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedWooId, setSelectedWooId] = useState<number | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editWarnings, setEditWarnings] = useState<readonly string[]>([]);
  const [preview, setPreview] = useState<PreviewResult | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setAppliedSearch(filters.search.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [filters.search]);

  const ordersQuery = useQuery({
    queryKey: ['shop-orders', filters.view, filters.assigned, filters.wooStatus, appliedSearch, page],
    queryFn: () =>
      shopJson<ShopOrdersResponse>(
        `/api/v1/shop-manager/orders?view=${filters.view}&assignedTo=${filters.assigned}&page=${page}&limit=${PAGE_SIZE}` +
          (filters.wooStatus !== 'all' ? `&wooStatus=${encodeURIComponent(filters.wooStatus)}` : '') +
          (appliedSearch !== '' ? `&search=${encodeURIComponent(appliedSearch)}` : ''),
      ),
    refetchInterval: 60_000,
  });

  const detailQuery = useQuery({
    queryKey: ['shop-order-detail', selectedWooId],
    queryFn: () => shopJson<ShopOrderDetail>(`/api/v1/shop-manager/orders/${selectedWooId as number}`),
    enabled: selectedWooId !== null,
  });

  const rows = useMemo(() => ordersQuery.data?.orders ?? [], [ordersQuery.data]);

  useEffect(() => {
    if (selectedWooId !== null && rows.some((row) => row.wooId === selectedWooId)) return;
    const first = rows[0];
    if (first !== undefined) {
      setSelectedWooId(first.wooId);
      setEditing(false);
      setEditError(null);
      setEditWarnings([]);
    }
  }, [rows, selectedWooId]);

  const invalidate = useCallback((): void => {
    void queryClient.invalidateQueries({ queryKey: ['shop-orders'] });
    void queryClient.invalidateQueries({ queryKey: ['shop-order-detail'] });
  }, [queryClient]);

  const run = useCallback(
    async (name: string, task: () => Promise<string>, advance = true): Promise<void> => {
      setBusy(name);
      setFlash(null);
      try {
        setFlash(await task());
        invalidate();
        if (advance) {
          const index = rows.findIndex((row) => row.wooId === selectedWooId);
          const next = rows[index + 1];
          if (next !== undefined) setSelectedWooId(next.wooId);
        }
      } catch (error) {
        setFlash(
          error instanceof ShopApiError && error.code === 'locked_by_other' && error.lockedBy !== null
            ? `Verrouillée par ${error.lockedBy}.`
            : `Erreur : ${(error as Error).message}`,
        );
        invalidate();
      } finally {
        setBusy(null);
      }
    },
    [invalidate, rows, selectedWooId],
  );

  const detail = detailQuery.data ?? null;
  const wooId = detail?.order.wooId ?? selectedWooId;

  const post = useCallback(
    (path: string, body?: unknown): Promise<unknown> => shopPost(path, body),
    [],
  );

  const previewMutation = useMutation({
    mutationFn: (patch: PreviewPayload) =>
      shopPatch<PreviewResponse>(`/api/v1/shop-manager/orders/${wooId as number}`, { ...patch, dryRun: true }),
    onSuccess: (result) => {
      setPreview({
        diff: result.diff,
        totalBefore: result.totalBefore,
        totalAfter: result.totalAfter === null ? null : result.totalAfter.toFixed(3),
      });
      setEditError(null);
    },
    onError: (error) => {
      setPreview(null);
      setEditError(error instanceof ShopApiError ? error.message : String(error));
    },
  });

  const editMutation = useMutation({
    mutationFn: (patch: EditorPatch) => shopPatch<EditResult>(`/api/v1/shop-manager/orders/${wooId as number}`, patch),
    onMutate: () => {
      setBusy('edit');
      setEditError(null);
    },
    onSuccess: (result) => {
      setEditWarnings(result.warnings);
      setEditing(false);
      setFlash(
        result.status === 'no_changes'
          ? 'Aucune modification à enregistrer.'
          : `Commande mise à jour (${result.changedFields.length} champ(s)).`,
      );
      invalidate();
    },
    onError: (error) => {
      setEditError(error instanceof ShopApiError ? error.message : String(error));
      if (error instanceof ShopApiError && error.code === 'woo_order_changed') invalidate();
    },
    onSettled: () => setBusy(null),
  });

  const syncMutation = useMutation({
    mutationFn: () => shopPost('/api/v1/shop-manager/sync'),
    onSuccess: () => setFlash('Synchronisation lancée — les commandes récentes arrivent sous ~30 s.'),
    onError: (error) => setFlash(`Erreur : ${(error as Error).message}`),
  });

  const onAttempt = (outcome: AttemptOutcome, notes: string, callbackAt: string): void => {
    if (wooId === null) return;
    const body: Record<string, unknown> = { outcome, notes };
    if (outcome === 'callback') body.callbackAt = callbackAt;
    void run(`attempt:${outcome}`, async () => {
      await post(`/api/v1/shop-manager/orders/${wooId}/attempt`, body);
      return outcome === 'confirmed'
        ? 'Commande confirmée.'
        : outcome === 'cancelled'
          ? 'Commande annulée (synchronisée sur Woo).'
          : `Appel enregistré — passage à la commande suivante.`;
    });
  };

  const actions: InspectorActions | null =
    detail === null || wooId === null
      ? null
      : {
          busy,
          flash,
          editing,
          savingEdit: editMutation.isPending,
          editError,
editWarnings,
          preview,
          onAttempt,
          onConfirm: () => {
            void run('confirm', async () => {
              await post(`/api/v1/shop-manager/orders/${wooId}/confirm`, {});
              return 'Commande confirmée.';
            });
          },
          onCancelOrder: (reason: string) => {
            void run('cancel', async () => {
              const result = (await post(`/api/v1/shop-manager/orders/${wooId}/cancel`, { reason })) as {
                wooSynced?: boolean;
                wooDetail?: string;
              };
              return result.wooSynced === true
                ? 'Annulée — Woo synchronisé.'
                : `Annulée localement. Woo : ${result.wooDetail ?? 'non synchronisé'}.`;
            });
          },
          onParcel: () => {
            void run('parcel', async () => {
              const result = (await post(`/api/v1/shop-manager/orders/${wooId}/parcel`, {})) as {
                codeBarre?: string;
              };
              return `Colis créé : ${result.codeBarre ?? '—'}.`;
            });
          },
          onAssign: () => {
            void run('assign', async () => {
              await post(`/api/v1/shop-manager/orders/${wooId}/assign`, {});
              await post(`/api/v1/shop-manager/orders/${wooId}/lock`, {});
              return 'Commande prise en charge et verrouillée.';
            });
          },
          onToggleLock: () => {
            const route = detail.order.locked ? 'unlock' : 'lock';
            void run(route, async () => {
              await post(`/api/v1/shop-manager/orders/${wooId}/${route}`, {});
              return route === 'lock' ? 'Verrouillée 15 min.' : 'Verrouillage levé.';
            });
          },
          onNotes: (payload) => {
            void run('notes', async () => {
              await post(`/api/v1/shop-manager/orders/${wooId}/notes`, payload);
              return 'Options enregistrées.';
            });
          },
          onToggleEdit: () => {
            setEditing((current) => !current);
            setEditError(null);
            setEditWarnings([]);
            setPreview(null);
          },
          onPreviewEdit: (patch) => previewMutation.mutate({ ...patch, dryRun: true }),
          onSaveEdit: (patch) => editMutation.mutate(patch),
        };

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      const typing =
        target !== null &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT');
      if (event.key === '/' && !typing) {
        event.preventDefault();
        searchRef.current?.focus();
        return;
      }
      if (typing) return;
      const index = rows.findIndex((row) => row.wooId === selectedWooId);
      if (event.key === 'j' || event.key === 'ArrowDown') {
        event.preventDefault();
        const next = rows[Math.min(rows.length - 1, index + 1)];
        if (next !== undefined) setSelectedWooId(next.wooId);
      }
      if (event.key === 'k' || event.key === 'ArrowUp') {
        event.preventDefault();
        const previous = rows[Math.max(0, index - 1)];
        if (previous !== undefined) setSelectedWooId(previous.wooId);
      }
      if (event.key === 'e') {
        event.preventDefault();
        setEditing((current) => !current);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rows, selectedWooId]);

  const patchFilters = (patch: Partial<QueueFilters>): void => {
    setFilters((current) => ({ ...current, ...patch }));
    if (!('search' in patch)) setPage(1);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageHeader
        index="S35"
        eyebrow="Gestion Logistique · Préparation"
        title="Shop Manager — Commandes à Confirmer & Expédier (COD)"
        purpose="Confirmer, corriger et expédier les commandes payées à la livraison."
        meta={[
          { label: 'File', value: `${ordersQuery.data?.total ?? 0}` },
          { label: 'En base', value: `${ordersQuery.data?.totalStored ?? 0}` },
          {
            label: 'Dernière sync',
            value:
              ordersQuery.data?.lastSyncedAt === null || ordersQuery.data?.lastSyncedAt === undefined
                ? 'jamais'
                : relative(ordersQuery.data.lastSyncedAt),
          },
        ]}
        actions={
          <button
            type="button"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-ink-soft hover:border-border-strong disabled:opacity-50"
          >
            ⟳ Synchroniser
          </button>
        }
      />

      {flash !== null && rows.length === 0 && (
        <div className="px-4 pt-3">
          <Alert tone="warn">{flash}</Alert>
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <QueueRail
          filters={filters}
          onChange={patchFilters}
          data={ordersQuery.data}
          onSync={syncMutation.mutate}
          syncing={syncMutation.isPending}
          searchRef={searchRef}
        />
        <OrderTable
          rows={rows}
          selectedWooId={selectedWooId}
          onSelect={(id) => {
            setSelectedWooId(id);
            setEditing(false);
            setEditError(null);
            setEditWarnings([]);
          }}
          loading={ordersQuery.isLoading}
          error={ordersQuery.error === null ? null : (ordersQuery.error as Error).message}
          page={page}
          pageSize={ordersQuery.data?.pageSize ?? PAGE_SIZE}
          total={ordersQuery.data?.total ?? 0}
          onPage={setPage}
        />
        {detailQuery.isLoading && <div className="w-[26rem] shrink-0 border-l border-border px-3 py-4"><Loading label="Chargement…" /></div>}
        {detailQuery.error !== null && (
          <div className="w-[26rem] shrink-0 border-l border-border px-3 py-4">
            <Alert tone="alarm">{(detailQuery.error as Error).message}</Alert>
          </div>
        )}
        {actions === null && !detailQuery.isLoading && detailQuery.error === null && (
          <div className="w-[26rem] shrink-0 border-l border-border px-3 py-4">
            <EmptyState title="Aucune commande sélectionnée" hint="Choisissez une ligne de la file." />
          </div>
        )}
        {actions !== null && detail !== null && <OrderInspector detail={detail} actions={actions} />}
      </div>
    </div>
  );
}